import { Suspense, useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { adminTheme } from "./adminTheme";
import { authSelector, setSidebarExpandStatus } from "../redux/slices/authSlice";
import { getWallet } from "../api/wallet";
import CustomerNavigation from "./sidebar/CustomerNavigation";
import CustomerTopbar from "./CustomerTopbar";
import useIsMobile from "../hooks/useIsMobile";
import layoutStyles from "./adminLayout.module.css";
import own from "./CustomerLayout.module.css";
import "./previewModals.css";
import "./previewTables.css";
import GlobalLoader from "../components/loader/GlobalLoader";
import WelcomeTour from "../components/welcome/WelcomeTour";
import { UsendLoadingMark } from "../components/loader/UsendMarks";

// Customer shell in the admin look (preview only, see utils/clientPreview).
export default function CustomerLayout({ children, announcement }) {
  const dispatch = useDispatch();
  const { sideBarExpand, loggedUser } = useSelector(authSelector);
  // Shopify refused our token (app removed or access revoked): orders stop syncing.
  const activeStore = loggedUser?.stores?.find((s) => String(s?._id) === String(loggedUser?.active_store));
  const disconnected = Boolean(activeStore?.token_error);
  // Shopify answers 404 "Store unavailable" when the store itself is closed.
  const storeClosed = /^HTTP 404/.test(activeStore?.token_error || "");
  const storeLabel = activeStore?.custom_store_name || activeStore?.store_name?.replace(/\.myshopify\.com$/, "") || "Your store";
  const mobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const expanded = mobile || sideBarExpand !== false;

  useEffect(() => {
    dispatch(getWallet());
  }, [dispatch]);

  // Modals render outside the layout, so the new look for them hangs off <body>.
  useEffect(() => {
    document.body.classList.add("usend-preview");
    return () => document.body.classList.remove("usend-preview");
  }, []);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  return (
    <ThemeProvider theme={adminTheme}>
      <GlobalLoader />
      <WelcomeTour />
      <div className={layoutStyles.shell}>
        <div className={`${layoutStyles.navWrap} ${drawerOpen ? layoutStyles.navOpen : ""}`}>
          <CustomerNavigation
            expanded={expanded}
            onToggle={() => (mobile ? setDrawerOpen(false) : dispatch(setSidebarExpandStatus(!expanded)))}
          />
        </div>
        <div
          className={`${layoutStyles.scrim} ${drawerOpen ? layoutStyles.scrimVisible : ""}`}
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
        <div
          className={`hide_scrollbar ${layoutStyles.card}`}
          style={{ background: "#f1f1f1", "--admin-page-bg": "#f1f1f1" }}
        >
          <CustomerTopbar onMenu={mobile ? () => setDrawerOpen(true) : undefined} />
          <main className={layoutStyles.main}>
            {disconnected && (
              <div className={own.disconnected} role="alert">
                <span className={own.alertIcon} aria-hidden="true">!</span>
                {storeClosed ? (
                  <p>
                    <strong>{storeLabel} is closed or unavailable on Shopify.</strong> Orders don't sync while the
                    store is closed. Reopened it? Reinstall the U-Send app to connect it again.
                  </p>
                ) : (
                  <p>
                    <strong>{storeLabel} isn't connected to U-Send anymore.</strong> Orders and products don't sync
                    until you reinstall the U-Send app in Shopify.
                  </p>
                )}
                <a className={own.alertAction} href="https://apps.shopify.com/u-send" target="_blank" rel="noreferrer">
                  Reinstall app
                </a>
              </div>
            )}
            {announcement?.announcement && (
              <div className={own.announcement} role="note">
                <span className={own.tag}>From U-Send</span>
                <p>{announcement.announcement}</p>
              </div>
            )}
            <Suspense
              fallback={
                <div style={{ height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <UsendLoadingMark size={32} />
                </div>
              }
            >
              {children}
            </Suspense>
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}
